import { Sheet, SheetContent, SheetHeader, SheetTitle } from "./ui/sheet";
import { useCart } from "../context/CartContext";
import { money } from "../lib/api";
import { Minus, Plus, X, ShoppingBag } from "lucide-react";
import { useNavigate } from "react-router-dom";

// Reference: Timeline slide-out cart drawer (line items | subtotal | checkout CTA)
export const CartDrawer = () => {
  const { items, open, setOpen, updateQty, removeItem, subtotal, count } = useCart();
  const navigate = useNavigate();

  const goToCheckout = () => {
    setOpen(false);
    navigate("/checkout");
  };

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-md bg-[#0B1024] border-l border-white/15 text-white p-0 flex flex-col rounded-none sm:rounded-l-[26px]"
        data-testid="cart-drawer"
      >
        <SheetHeader className="px-6 py-5 border-b border-white/15">
          <SheetTitle className="font-display font-bold text-lg tracking-tighter uppercase text-left text-white">
            Your Cart ({count})
          </SheetTitle>
        </SheetHeader>

        {items.length === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center gap-4 text-center px-8">
            <ShoppingBag size={40} strokeWidth={1} className="text-white/60" />
            <p className="text-sm tracking-[0.15em] uppercase text-white/40">
              Your cart is empty
            </p>
            <button
              onClick={() => setOpen(false)}
              className="text-xs tracking-[0.2em] uppercase font-bold border-b border-electric text-electric pb-1"
              data-testid="cart-continue-shopping"
            >
              Continue Shopping
            </button>
          </div>
        ) : (
          <>
            <div className="flex-1 overflow-y-auto">
              {items.map((i) => (
                <div
                  key={i.id}
                  className="flex gap-4 p-6 border-b border-white/12"
                  data-testid={`cart-item-${i.product.slug}`}
                >
                  <div className="w-20 h-20 border border-white/15 shrink-0 bg-[#121A3A] overflow-hidden sq-md">
                    <img
                      src={i.variantImage || i.product.image}
                      alt={i.product.name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex justify-between gap-2">
                      <h3 className="font-display font-bold text-sm uppercase tracking-tight leading-tight text-white">
                        {i.product.name}{i.variantLabel ? ` · ${i.variantLabel}` : ""}
                      </h3>
                      <button
                        onClick={() => removeItem(i.id)}
                        aria-label="Remove item"
                        className="text-white/70 hover:text-electric"
                        data-testid={`cart-remove-${i.product.slug}`}
                      >
                        <X size={16} />
                      </button>
                    </div>
                    <p className="text-xs text-electric-400 mt-1">
                      {i.tierQty > 1 ? `${i.tierQty} Pack` : "Single vial"}
                    </p>
                    <div className="flex justify-between items-center mt-3">
                      <div className="flex items-center border border-white/20 sq-sm overflow-hidden">
                        <button
                          onClick={() => updateQty(i.id, i.quantity - 1)}
                          className="w-8 h-8 flex items-center justify-center text-white hover:bg-electric hover:text-black transition-colors duration-200"
                          data-testid={`cart-dec-${i.product.slug}`}
                        >
                          <Minus size={12} />
                        </button>
                        <span className="w-8 text-center text-sm font-bold text-white">{i.quantity}</span>
                        <button
                          onClick={() => updateQty(i.id, i.quantity + 1)}
                          className="w-8 h-8 flex items-center justify-center text-white hover:bg-electric hover:text-black transition-colors duration-200"
                          data-testid={`cart-inc-${i.product.slug}`}
                        >
                          <Plus size={12} />
                        </button>
                      </div>
                      <span className="font-display font-bold text-sm text-white">
                        {money(i.packPrice * i.quantity)}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="border-t border-white/15 p-6 space-y-4">
              <div className="flex justify-between items-baseline">
                <span className="text-xs tracking-[0.2em] uppercase font-bold text-white">Subtotal</span>
                <span className="font-display font-bold text-2xl tracking-tighter text-white" data-testid="cart-subtotal">
                  {money(subtotal)}
                </span>
              </div>
              <p className="text-[10px] tracking-[0.15em] uppercase text-white/40">
                Shipping and taxes calculated at checkout
              </p>
              <button
                onClick={goToCheckout}
                className="btn-primary w-full py-4 text-xs tracking-[0.2em] uppercase font-bold"
                data-testid="checkout-button"
              >
                Checkout
              </button>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
};
